import { formatMoney, QUOTE_STATUS_LABELS, type QuoteStatus } from '@mt/shared';
import { Code2, Copy, FileCheck2, Globe, Mail, MessageCircle, PhoneCall, Plug, PlugZap, QrCode, Search, Send, Trash2 } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label, NativeSelect, Textarea } from '@/components/ui/input';
import { EmptyState, Spinner } from '@/components/ui/states';
import { useAuth } from '@/lib/auth';
import type { Customer } from '@/lib/types';
import { cn } from '@/lib/utils';
import { searchCustomers, useUsers } from '../quotes/api';
import { BotSettingsSection, MyBotSection } from './BotPanel';
import { EmailConnectForm } from './EmailConnect';
import {
  CHANNEL_LABELS,
  embedCode,
  getQrLogin,
  lookupPhone,
  startChannelConnect,
  useChannelSettings,
  useCreateWebChat,
  useUpdateWebChat,
  useWebChat,
  useCancelQr,
  useConnectAccount,
  useCreateAccount,
  useDisconnectAccount,
  useSetPersonal,
  useLinkCustomer,
  useOpenByPhone,
  useAssign,
  useConnectPhone,
  usePhoneSettings,
  useRemoveAccount,
  useSendableQuotes,
  useSendQuote,
  useInboxCertificates,
  useSendCertificates,
  type InboxCert,
  useSetInternal,
  useStaffOptions,
  useStartQr,
  useZaloAccounts,
  ZALO_STATUS_LABELS,
  type InboxChannel,
  type PhoneLookup,
  type QrLoginState,
  type ZaloAccount,
  type ZaloConversation,
} from './api';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Có lỗi, thử lại sau');
const fail = (e: unknown) => toast.error(errMsg(e));

export function StatusDot({ status, className }: { status: ZaloAccount['status']; className?: string }) {
  return (
    <span
      className={cn(
        'inline-block size-2 shrink-0 rounded-full',
        status === 'connected' ? 'bg-success' : status === 'connecting' ? 'bg-warning' : status === 'disconnected' ? 'bg-muted-foreground' : 'bg-error',
        className,
      )}
    />
  );
}

/** Nhãn kênh nhỏ cạnh tên (Zalo cá nhân không gắn nhãn cho gọn). */
export function ChannelBadge({ channel, className }: { channel: InboxChannel; className?: string }) {
  if (channel === 'zalo') return null;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded px-1 text-[10px] font-semibold leading-4',
        channel === 'messenger'
          ? 'bg-info/15 text-info'
          : channel === 'zalo_oa'
            ? 'bg-primary/15 text-primary'
            : channel === 'email'
              ? 'bg-warning/15 text-warning'
              : channel === 'phone'
                ? 'bg-success/15 text-success'
                : 'bg-muted text-muted-foreground',
        className,
      )}
    >
      {CHANNEL_LABELS[channel]}
    </span>
  );
}

const goConnect = (kind: 'zalo-oa' | 'messenger') =>
  startChannelConnect(kind).then((r) => {
    window.location.href = r.url;
  }, fail);

function CopyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
      <code className="min-w-0 flex-1 truncate rounded bg-muted px-1.5 py-0.5">{value}</code>
      <Button
        size="icon"
        variant="ghost"
        className="size-6"
        title="Chép"
        onClick={() => navigator.clipboard.writeText(value).then(() => toast.success('Đã chép'), fail)}
      >
        <Copy />
      </Button>
    </div>
  );
}

const splitOrigins = (v: string) => v.split(/[\s,;]+/).filter(Boolean);

/** Tạo / sửa khung chat website; có id thì hiện mã nhúng. */
function WebChatForm({ id, initialName, onDone }: { id: number | null; initialName?: string; onDone: () => void }) {
  const cur = useWebChat(id);
  const create = useCreateWebChat();
  const update = useUpdateWebChat();
  const [name, setName] = useState(initialName ?? 'Chat website');
  const [origins, setOrigins] = useState('');
  const [greeting, setGreeting] = useState('');
  useEffect(() => {
    if (cur.data) {
      setOrigins(cur.data.origins.join(', '));
      setGreeting(cur.data.greeting);
    }
  }, [cur.data]);
  const save = () => {
    const body = { name: name.trim(), origins: splitOrigins(origins), greeting: greeting.trim() || null };
    const p = id ? update.mutateAsync({ id, ...body }) : create.mutateAsync(body);
    return p.then(() => {
      toast.success(id ? 'Đã lưu khung chat' : 'Đã tạo khung chat, chép mã nhúng dán vào website');
      if (id) onDone();
    }, fail);
  };
  return (
    <div className="space-y-2 rounded-md bg-muted/40 p-2.5">
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label htmlFor="wc-name">Tên khung chat</Label>
          <Input id="wc-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </div>
        <div>
          <Label htmlFor="wc-origins">Tên miền website</Label>
          <Input id="wc-origins" value={origins} onChange={(e) => setOrigins(e.target.value)} placeholder="thepminhtien.com" />
        </div>
      </div>
      <div>
        <Label htmlFor="wc-greeting">Lời chào</Label>
        <Textarea
          id="wc-greeting"
          rows={2}
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
          maxLength={500}
          placeholder="Để trống = lời chào mặc định"
        />
      </div>
      {cur.data && <CopyField label="Mã nhúng" value={embedCode(cur.data.key)} />}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Đóng
        </Button>
        <Button size="sm" disabled={!name.trim() || create.isPending || update.isPending} onClick={() => void save()}>
          {id ? 'Lưu' : 'Tạo khung chat'}
        </Button>
      </div>
    </div>
  );
}

/** GĐ / quản trị: kết nối Zalo OA và Fanpage công ty; hiện link cần dán vào trang nhà phát triển Zalo / Facebook. */
function CompanyChannels({ webEdit, setWebEdit }: { webEdit: number | 'new' | null; setWebEdit: (v: number | 'new' | null) => void }) {
  const s = useChannelSettings(true);
  if (!s.data) return null;
  const { zaloOa, messenger } = s.data;
  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="text-sm font-medium">Kênh công ty (GĐ / quản trị)</div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm">Zalo Official Account</span>
          <Button size="sm" variant="outline" disabled={!zaloOa.configured} onClick={() => void goConnect('zalo-oa')}>
            <PlugZap /> Kết nối Zalo OA
          </Button>
        </div>
        {zaloOa.configured ? (
          <>
            <CopyField label="Callback URL" value={zaloOa.callbackUrl} />
            <CopyField label="Webhook URL" value={zaloOa.webhookUrl} />
            {!zaloOa.webhookSigned && <p className="text-xs text-warning">Chưa đặt ZALO_OA_WEBHOOK_SECRET: ERP chưa nhận tin đến của OA.</p>}
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Chưa bật: cần ZALO_OA_APP_ID, ZALO_OA_APP_SECRET, ZALO_OA_WEBHOOK_SECRET trong .env máy chủ.</p>
        )}
      </div>
      <div className="space-y-1.5 border-t pt-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm">Fanpage Facebook (Messenger)</span>
          <Button size="sm" variant="outline" disabled={!messenger.configured} onClick={() => void goConnect('messenger')}>
            <MessageCircle /> Kết nối Fanpage
          </Button>
        </div>
        {messenger.configured ? (
          <>
            <CopyField label="Redirect URI" value={messenger.callbackUrl} />
            <CopyField label="Webhook URL" value={messenger.webhookUrl} />
            {messenger.verifyToken && <CopyField label="Verify token" value={messenger.verifyToken} />}
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Chưa bật: cần FACEBOOK_APP_ID, FACEBOOK_APP_SECRET trong .env máy chủ.</p>
        )}
      </div>
      <div className="space-y-1.5 border-t pt-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm">Khung chat website</span>
          {webEdit !== 'new' && (
            <Button size="sm" variant="outline" onClick={() => setWebEdit('new')}>
              <Globe /> Tạo khung chat
            </Button>
          )}
        </div>
        {webEdit === 'new' ? (
          <WebChatForm id={null} onDone={() => setWebEdit(null)} />
        ) : (
          <p className="text-xs text-muted-foreground">
            Khách trên website bấm nút chat góc phải dưới, tin vào hộp thư này. Bấm "Mã nhúng" ở khung chat để lấy dòng script dán vào website.
          </p>
        )}
      </div>
      <PhoneSection />
      <BotSettingsSection />
    </div>
  );
}

function PhoneReconnect() {
  const connect = useConnectPhone();
  return (
    <Button size="sm" variant="outline" disabled={connect.isPending} onClick={() => connect.mutateAsync().catch(fail)}>
      <PlugZap /> Kết nối lại
    </Button>
  );
}

/** Tổng đài Stringee (bước 6, DEC-234): định tuyến cuộc gọi cài trên Stringee; ERP đọc nhật ký mỗi 2 phút. */
function PhoneSection() {
  const s = usePhoneSettings();
  const connect = useConnectPhone();
  const d = s.data;
  if (!d) return null;
  return (
    <div className="space-y-1.5 border-t pt-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm">Tổng đài Stringee{d.number ? ` (${d.number})` : ''}</span>
        {!d.connected && (
          <Button
            size="sm"
            variant="outline"
            disabled={!d.configured || connect.isPending}
            onClick={() => connect.mutateAsync().then(() => toast.success('Đã kết nối tổng đài'), fail)}
          >
            <PhoneCall /> Kết nối tổng đài
          </Button>
        )}
      </div>
      {!d.configured ? (
        <p className="text-xs text-muted-foreground">Chưa bật: cần STRINGEE_API_KEY_SID, STRINGEE_API_KEY_SECRET, STRINGEE_NUMBER trong .env máy chủ.</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Cuộc gọi đến đổ máy nào, lời chào và hộp thư thoại ngoài giờ cài trên trang Stringee. ERP đọc nhật ký cuộc gọi mỗi 2 phút: cuộc gọi nhỡ hiện như tin
          khách chờ, có ghi âm thì nghe ngay trong hội thoại.
          {d.lastSyncAt && ` Đọc lần cuối ${new Date(d.lastSyncAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}.`}
        </p>
      )}
      {d.lastError && <p className="text-xs text-error">{d.lastError}</p>}
    </div>
  );
}

// ───────────────────────── Nick Zalo + quét QR ─────────────────────────

const QR_STAGE_TEXT: Record<QrLoginState['stage'], string> = {
  none: 'Đang tạo mã…',
  waiting: 'Mở Zalo trên điện thoại → biểu tượng QR → quét mã này',
  scanned: 'Đã quét, bấm "Đăng nhập" trên điện thoại',
  connected: 'Đã kết nối',
  expired: 'Mã đã hết hạn',
  declined: 'Bạn đã từ chối trên điện thoại',
  error: 'Không đăng nhập được',
};

function QrPanel({ account, onDone }: { account: ZaloAccount; onDone: () => void }) {
  const [state, setState] = useState<QrLoginState | null>(null);
  const [round, setRound] = useState(0);
  const start = useStartQr();
  const cancel = useCancelQr();

  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const s = await getQrLogin(account.id);
        if (stop) return;
        setState(s);
        if (s.stage === 'connected') {
          toast.success(`Đã kết nối Zalo ${s.account.displayName ?? s.account.name}`);
          onDone();
          return;
        }
        if (s.stage === 'expired' || s.stage === 'declined' || s.stage === 'error') return;
      } catch {
        /* thử lại lượt sau */
      }
      timer = setTimeout(poll, 1500);
    };
    setState(null);
    start.mutateAsync(account.id).then(poll, fail);
    return () => {
      stop = true;
      clearTimeout(timer);
    };
    // mỗi lượt quét (round) tạo mã mới 1 lần
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account.id, round]);

  const stage = state?.stage ?? 'none';
  const ended = stage === 'expired' || stage === 'declined' || stage === 'error';
  return (
    <div className="grid gap-4 sm:grid-cols-[200px_1fr] sm:items-center">
      <div className="flex size-[200px] items-center justify-center rounded-lg border bg-white p-2">
        {state?.image && !ended ? (
          <img src={state.image} alt="Mã QR đăng nhập Zalo" className="size-full" />
        ) : ended ? (
          <QrCode className="size-16 text-muted-foreground/40" />
        ) : (
          <Spinner className="size-6" />
        )}
      </div>
      <div className="space-y-3 text-sm">
        <div className="font-medium">
          {stage === 'scanned' && state?.displayName ? `${state.displayName}: ` : ''}
          {QR_STAGE_TEXT[stage]}
        </div>
        {state?.error && <p className="text-xs text-destructive">{state.error}</p>}
        <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
          Mỗi nick Zalo chỉ giữ 1 phiên trên máy tính. Đăng nhập ở ERP sẽ thoát phiên Zalo Web / Minhtien-CRM của nick này.
        </p>
        <div className="flex gap-2">
          {ended && (
            <Button size="sm" onClick={() => setRound((r) => r + 1)}>
              Tạo mã mới
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => cancel.mutateAsync(account.id).then(onDone, fail)}>
            Hủy
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AccountsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, can } = useAuth();
  const accounts = useZaloAccounts(open);
  const create = useCreateAccount();
  const remove = useRemoveAccount();
  const connect = useConnectAccount();
  const disconnect = useDisconnectAccount();
  const setPersonal = useSetPersonal();
  const [name, setName] = useState('');
  const [qrFor, setQrFor] = useState<ZaloAccount | null>(null);
  const [webEdit, setWebEdit] = useState<number | 'new' | null>(null);
  const [emailFor, setEmailFor] = useState<string | null>(null);
  const items = accounts.data ?? [];
  const mine = (a: ZaloAccount) => a.ownerUserId === user?.id || can('zalo.manage');

  useEffect(() => {
    if (!open) {
      setQrFor(null);
      setWebEdit(null);
      setEmailFor(null);
    }
  }, [open]);

  const add = () =>
    create.mutateAsync(name.trim()).then((a) => {
      setName('');
      setQrFor(a);
    }, fail);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Kênh nhắn tin"
      description="Nick Zalo cá nhân (quét mã QR) và email riêng của nhân viên, Zalo OA, Fanpage, khung chat website của công ty"
      className="max-w-2xl"
    >
      {qrFor ? (
        <QrPanel account={qrFor} onDone={() => setQrFor(null)} />
      ) : (
        <div className="space-y-4">
          {accounts.isLoading ? (
            <Spinner />
          ) : items.length === 0 ? (
            <EmptyState title="Chưa có nick Zalo" description="Đặt tên nick (VD: Zalo KD Hưng) rồi quét mã QR bằng Zalo trên điện thoại." />
          ) : (
            <ul className="divide-y rounded-lg border">
              {items.map((a) => (
                <Fragment key={a.id}>
                  <li className="flex items-center gap-3 px-3 py-2.5">
                    {a.avatarUrl ? (
                      <img src={a.avatarUrl} alt="" className="size-9 rounded-full object-cover" />
                    ) : (
                      <span className="flex size-9 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                        {initials(a.displayName ?? a.name)}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 truncate text-sm font-medium">
                        <ChannelBadge channel={a.channel} />
                        {a.name}
                        {a.displayName && a.displayName !== a.name && <span className="font-normal text-muted-foreground"> · {a.displayName}</span>}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <StatusDot status={a.status} />
                        {ZALO_STATUS_LABELS[a.status]} · {a.ownerName}
                      </div>
                      {a.lastError && a.status !== 'connected' && <div className="truncate text-xs text-destructive">{a.lastError}</div>}
                      {a.channel === 'zalo' &&
                        (a.ownerUserId === user?.id ? (
                          <label
                            className="mt-0.5 flex w-fit cursor-pointer items-center gap-1.5 text-xs text-muted-foreground"
                            title="Nick Zalo riêng của bạn: Giám đốc, Trưởng phòng chỉ xem chat với khách / NCC; chat bạn bè, gia đình chỉ bạn xem; không kéo lịch sử cũ"
                          >
                            <input
                              type="checkbox"
                              className="size-3.5 accent-primary"
                              checked={a.isPersonal}
                              disabled={setPersonal.isPending}
                              onChange={(e) =>
                                setPersonal
                                  .mutateAsync({ id: a.id, personal: e.target.checked })
                                  .then(() => toast.success(e.target.checked ? 'Đã đánh dấu nick cá nhân' : 'Đã đánh dấu nick công ty'))
                                  .catch(fail)
                              }
                            />
                            Nick cá nhân (chỉ chia sẻ chat với khách)
                          </label>
                        ) : (
                          a.isPersonal && <div className="text-xs text-muted-foreground">Nick cá nhân: chỉ thấy chat với khách / NCC</div>
                        ))}
                    </div>
                    {mine(a) && (
                      <div className="flex shrink-0 gap-1">
                        {a.channel === 'web' ? (
                          <>
                            <Button size="sm" variant="ghost" title="Mã nhúng, tên miền, lời chào" onClick={() => setWebEdit(webEdit === a.id ? null : a.id)}>
                              <Code2 /> Mã nhúng
                            </Button>
                            {a.status === 'connected' ? (
                              <Button size="sm" variant="ghost" title="Tắt khung chat" onClick={() => disconnect.mutateAsync(a.id).catch(fail)}>
                                <Plug /> Tắt
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => connect.mutateAsync(a.id).catch(fail)}>
                                <PlugZap /> Bật
                              </Button>
                            )}
                          </>
                        ) : a.channel === 'email' ? (
                          a.status === 'connected' ? (
                            <Button size="sm" variant="ghost" title="Ngắt kết nối" onClick={() => disconnect.mutateAsync(a.id).catch(fail)}>
                              <Plug /> Ngắt
                            </Button>
                          ) : (
                            a.ownerUserId === user?.id && (
                              <Button size="sm" variant="outline" onClick={() => setEmailFor(a.ownId ?? '')}>
                                <PlugZap /> Kết nối lại
                              </Button>
                            )
                          )
                        ) : a.channel === 'phone' ? (
                          a.status === 'connected' ? (
                            <Button size="sm" variant="ghost" title="Ngắt kết nối" onClick={() => disconnect.mutateAsync(a.id).catch(fail)}>
                              <Plug /> Ngắt
                            </Button>
                          ) : (
                            can('zalo.manage') && <PhoneReconnect />
                          )
                        ) : a.channel !== 'zalo' ? (
                          a.status === 'connected' ? (
                            <Button size="sm" variant="ghost" title="Ngắt kết nối" onClick={() => disconnect.mutateAsync(a.id).catch(fail)}>
                              <Plug /> Ngắt
                            </Button>
                          ) : (
                            can('zalo.manage') && (
                              <Button size="sm" variant="outline" onClick={() => void goConnect(a.channel === 'messenger' ? 'messenger' : 'zalo-oa')}>
                                <PlugZap /> Kết nối lại
                              </Button>
                            )
                          )
                        ) : a.status === 'connected' ? (
                          <Button size="sm" variant="ghost" title="Ngắt kết nối" onClick={() => disconnect.mutateAsync(a.id).catch(fail)}>
                            <Plug /> Ngắt
                          </Button>
                        ) : (
                          <>
                            {a.hasSession && (
                              <Button size="sm" variant="outline" disabled={connect.isPending} onClick={() => connect.mutateAsync(a.id).catch(fail)}>
                                <PlugZap /> Kết nối
                              </Button>
                            )}
                            <Button size="sm" variant={a.hasSession ? 'ghost' : 'default'} onClick={() => setQrFor(a)}>
                              <QrCode /> Quét QR
                            </Button>
                          </>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Bỏ nick khỏi ERP"
                          onClick={() => {
                            if (confirm(`Bỏ nick "${a.name}" khỏi ERP? Lịch sử tin nhắn vẫn được giữ.`)) remove.mutateAsync(a.id).catch(fail);
                          }}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    )}
                  </li>
                  {webEdit === a.id && (
                    <li className="px-3 pb-3">
                      <WebChatForm id={a.id} initialName={a.name} onDone={() => setWebEdit(null)} />
                    </li>
                  )}
                </Fragment>
              ))}
            </ul>
          )}
          {emailFor !== null ? (
            <EmailConnectForm initialEmail={emailFor || undefined} onDone={() => setEmailFor(null)} />
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEmailFor('')}>
              <Mail /> Kết nối email của tôi
            </Button>
          )}
          {can('zalo.manage') ? <CompanyChannels webEdit={webEdit} setWebEdit={setWebEdit} /> : null}
          <MyBotSection />
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) void add();
            }}
          >
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tên nick mới, VD: Zalo KD Hưng" maxLength={120} />
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              <QrCode /> Thêm và quét QR
            </Button>
          </form>
        </div>
      )}
    </Dialog>
  );
}

// ───────────────────────── Tin nhắn mới theo SĐT ─────────────────────────

export function NewChatDialog({ open, onClose, onOpened }: { open: boolean; onClose: () => void; onOpened: (c: ZaloConversation) => void }) {
  const { user, can } = useAuth();
  const accounts = useZaloAccounts(open);
  // Chỉ nhắn từ nick của mình (TP xem được nick NV nhưng không dùng nick đó mở hội thoại mới)
  const connected = (accounts.data ?? []).filter((a) => a.channel === 'zalo' && a.status === 'connected' && (a.ownerUserId === user?.id || can('zalo.manage')));
  const [accountId, setAccountId] = useState<number | null>(null);
  const [phone, setPhone] = useState('');
  const [found, setFound] = useState<PhoneLookup | null>(null);
  const [looking, setLooking] = useState(false);
  const [friend, setFriend] = useState(true);
  const [greeting, setGreeting] = useState('');
  const openChat = useOpenByPhone();
  const acc = accountId ?? connected.find((a) => a.ownerUserId === user?.id)?.id ?? connected[0]?.id ?? null;

  useEffect(() => {
    if (open) {
      setPhone('');
      setFound(null);
      setGreeting('');
      setFriend(true);
    }
  }, [open]);

  const lookup = async () => {
    if (!acc || !phone.trim()) return;
    setLooking(true);
    setFound(null);
    try {
      setFound(await lookupPhone(acc, phone));
    } catch (e) {
      fail(e);
    } finally {
      setLooking(false);
    }
  };

  const submit = () =>
    acc &&
    openChat.mutateAsync({ accountId: acc, phone, friendRequest: friend, greeting: greeting.trim() || null }).then((c) => {
      if (friend) toast.success('Đã gửi lời mời kết bạn');
      onOpened(c);
      onClose();
    }, fail);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Tin nhắn mới"
      description="Tìm Zalo của khách theo số điện thoại"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Hủy
          </Button>
          <Button disabled={!found || openChat.isPending} onClick={() => void submit()}>
            {openChat.isPending && <Spinner />} Mở hội thoại
          </Button>
        </>
      }
    >
      {connected.length === 0 ? (
        <EmptyState title="Chưa có nick Zalo nào đang kết nối" description="Mở mục Nick Zalo để quét mã QR trước." />
      ) : (
        <div className="space-y-3">
          <div>
            <Label>Gửi từ nick</Label>
            <NativeSelect value={acc ?? ''} onChange={(e) => setAccountId(Number(e.target.value))}>
              {connected.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.displayName ? ` · ${a.displayName}` : ''}
                </option>
              ))}
            </NativeSelect>
          </div>
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void lookup();
            }}
          >
            <div className="flex-1">
              <Label htmlFor="zalo-phone">Số điện thoại</Label>
              <Input
                id="zalo-phone"
                autoFocus
                inputMode="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setFound(null);
                }}
                placeholder="0912 345 678"
              />
            </div>
            <Button type="submit" variant="outline" disabled={!phone.trim() || looking}>
              {looking ? <Spinner /> : <Search />} Tìm
            </Button>
          </form>
          {found && (
            <div className="flex items-center gap-3 rounded-lg border bg-muted/40 p-3">
              {found.avatarUrl ? (
                <img src={found.avatarUrl} alt="" className="size-10 rounded-full object-cover" />
              ) : (
                <span className="flex size-10 items-center justify-center rounded-full bg-muted text-xs font-semibold">{initials(found.name)}</span>
              )}
              <div className="min-w-0 text-sm">
                <div className="font-medium">{found.name ?? found.phone} · có Zalo</div>
                <div className="text-xs text-muted-foreground">
                  {found.customerId ? 'Khớp một khách hàng ERP theo SĐT, tự gắn khi mở hội thoại' : 'Chưa khớp khách hàng ERP'}
                </div>
              </div>
            </div>
          )}
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5" checked={friend} onChange={(e) => setFriend(e.target.checked)} />
            <span>
              Gửi lời mời kết bạn
              <span className="block text-xs text-muted-foreground">Người lạ vẫn nhắn được, nhưng Zalo giới hạn số tin gửi cho người chưa kết bạn.</span>
            </span>
          </label>
          {friend && (
            <Textarea
              rows={2}
              maxLength={150}
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              placeholder={`Chào Anh/Chị, em là ${user?.fullName ?? ''} bên Thép Minh Tiến…`}
            />
          )}
        </div>
      )}
    </Dialog>
  );
}

// ───────────────────────── Gửi báo giá PDF ─────────────────────────

export function SendQuoteDialog({ conversation, open, onClose }: { conversation: ZaloConversation; open: boolean; onClose: () => void }) {
  const quotes = useSendableQuotes(conversation.id, open);
  const send = useSendQuote();
  const [pick, setPick] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const items = quotes.data?.items ?? [];
  const sel = items.find((q) => q.id === pick) ?? items[0] ?? null;

  useEffect(() => {
    if (open) {
      setPick(null);
      setMessage('');
    }
  }, [open]);

  const submit = () =>
    sel &&
    send.mutateAsync({ id: conversation.id, quoteId: sel.id, version: sel.version, message: message.trim() || null }).then((r) => {
      toast.success(`Đã gửi báo giá ${r.quoteCode} qua ${CHANNEL_LABELS[conversation.channel]}`);
      onClose();
    }, fail);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Gửi báo giá qua Zalo"
      description={conversation.customerCode ? `${conversation.customerCode} · ${conversation.customerName}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Hủy
          </Button>
          <Button disabled={!sel || send.isPending} onClick={() => void submit()}>
            {send.isPending ? <Spinner /> : <Send />} Gửi PDF
          </Button>
        </>
      }
    >
      {!conversation.customerId ? (
        <EmptyState title="Hội thoại chưa gắn khách hàng" description="Gắn khách hàng ERP trước, rồi chọn báo giá để gửi." />
      ) : quotes.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <EmptyState title="Khách chưa có báo giá gửi được" description="Chỉ gửi được báo giá ERP ở trạng thái Nháp, Đã gửi khách hoặc Đang đàm phán." />
      ) : (
        <div className="space-y-3">
          <div className="space-y-2" role="radiogroup">
            {items.map((q) => (
              <button
                key={q.id}
                type="button"
                role="radio"
                aria-checked={sel?.id === q.id}
                onClick={() => setPick(q.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border p-3 text-left',
                  sel?.id === q.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
                )}
              >
                <span
                  className={cn('size-4 shrink-0 rounded-full border-2', sel?.id === q.id ? 'border-[5px] border-primary' : 'border-muted-foreground/50')}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {q.code}
                    {q.projectName ? ` · ${q.projectName}` : ''}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {new Date(q.quoteDate).toLocaleDateString('vi-VN')} · {QUOTE_STATUS_LABELS[q.status as QuoteStatus] ?? q.status}
                  </span>
                </span>
                <span className="text-sm font-medium tabular-nums">{formatMoney(q.grandTotal)} đ</span>
              </button>
            ))}
          </div>
          <div>
            <Label>Lời nhắn</Label>
            <Textarea
              rows={3}
              maxLength={1000}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={sel ? `Em gửi Anh/Chị báo giá ${sel.code}. Anh/Chị xem giúp em, cần điều chỉnh gì em gửi lại ngay ạ.` : ''}
            />
          </div>
          <p className="text-xs text-muted-foreground">Gửi file PDF; báo giá chuyển sang Đã gửi khách (kênh Zalo) và lưu bản gửi, như nút Gửi khách.</p>
        </div>
      )}
    </Dialog>
  );
}

// ───────────────────────── Gắn khách, giao NV ─────────────────────────

export function LinkCustomerDialog({ conversation, open, onClose }: { conversation: ZaloConversation; open: boolean; onClose: () => void }) {
  const link = useLinkCustomer();
  const [text, setText] = useState('');
  const save = (customerId: number | null) =>
    link.mutateAsync({ id: conversation.id, customerId }).then(() => {
      toast.success(customerId ? 'Đã gắn khách hàng' : 'Đã bỏ gắn khách hàng');
      onClose();
    }, fail);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Gắn khách hàng ERP"
      description={conversation.contactName ?? conversation.phone ?? undefined}
    >
      <div className="space-y-3">
        <Combobox<Customer>
          text={text}
          onTextChange={setText}
          search={(q) => searchCustomers(q).then((r) => r.items)}
          onSelect={(c) => void save(c.id)}
          getKey={(c) => c.id}
          renderItem={(c) => (
            <div>
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-muted-foreground">{[c.code, c.taxCode && `MST ${c.taxCode}`, c.phone].filter(Boolean).join(' · ')}</div>
            </div>
          )}
          placeholder="Gõ tên, mã, MST, SĐT…"
          dropdownWidth={440}
        />
        {conversation.customerId && (
          <Button variant="ghost" size="sm" onClick={() => void save(null)}>
            Bỏ gắn {conversation.customerCode}
          </Button>
        )}
      </div>
    </Dialog>
  );
}

export function AssignDialog({ conversation, open, onClose }: { conversation: ZaloConversation; open: boolean; onClose: () => void }) {
  const users = useUsers();
  const assign = useAssign();
  const [userId, setUserId] = useState<number | null>(conversation.assignedUserId);
  useEffect(() => {
    if (open) setUserId(conversation.assignedUserId);
  }, [open, conversation.assignedUserId]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Giao hội thoại"
      description="Người được giao thấy và trả lời hội thoại này"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Hủy
          </Button>
          <Button
            disabled={assign.isPending}
            onClick={() =>
              assign.mutateAsync({ id: conversation.id, userId }).then(() => {
                toast.success('Đã giao hội thoại');
                onClose();
              }, fail)
            }
          >
            Lưu
          </Button>
        </>
      }
    >
      <NativeSelect value={userId ?? ''} onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : null)}>
        <option value="">Chưa giao</option>
        {(users.data ?? []).map((u) => (
          <option key={u.id} value={u.id}>
            {u.fullName}
          </option>
        ))}
      </NativeSelect>
    </Dialog>
  );
}

/** Gán người Zalo là Nhân viên CTY (theo danh sách nhân sự, cả NV không dùng ERP) hoặc nội bộ: trợ lý ngoài giờ không trả lời. */
export function InternalDialog({ conversation, open, onClose }: { conversation: ZaloConversation; open: boolean; onClose: () => void }) {
  const staff = useStaffOptions(open);
  const setInternal = useSetInternal();
  const [employeeId, setEmployeeId] = useState<number | null>(conversation.employeeId);
  useEffect(() => {
    if (open) setEmployeeId(conversation.employeeId);
  }, [open, conversation.employeeId]);
  const save = (internal: boolean) =>
    setInternal.mutateAsync({ id: conversation.id, internal, employeeId: internal ? employeeId : null }).then(() => {
      toast.success(internal ? 'Đã gán Nội bộ / Nhân viên CTY, trợ lý không trả lời nữa' : 'Đã bỏ gán, trợ lý trả lời lại');
      onClose();
    }, fail);
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Nội bộ / Nhân viên CTY"
      description="Người này là nhân viên công ty (kể cả NV không dùng ERP, chỉ có Zalo): trợ lý ngoài giờ không trả lời. Áp cho mọi nick công ty."
      footer={
        <>
          {conversation.isInternal && (
            <Button variant="ghost" disabled={setInternal.isPending} onClick={() => save(false)}>
              Bỏ gán
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Hủy
          </Button>
          <Button disabled={setInternal.isPending} onClick={() => save(true)}>
            Lưu
          </Button>
        </>
      }
    >
      <Label className="text-xs">Nhân viên công ty</Label>
      <NativeSelect value={employeeId ?? ''} onChange={(e) => setEmployeeId(e.target.value ? Number(e.target.value) : null)} aria-label="Nhân viên công ty">
        <option value="">Nội bộ (không chọn nhân viên)</option>
        {(staff.data?.items ?? []).map((u) => (
          <option key={u.id} value={u.id}>
            {u.fullName} ({u.code})
          </option>
        ))}
      </NativeSelect>
    </Dialog>
  );
}

export const initials = (name: string | null | undefined) =>
  (name ?? '?')
    .trim()
    .split(/\s+/)
    .slice(-2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

// ───────────────────────── Gửi CO/CQ hàng hóa (P24 PR 1b, DEC-459) ─────────────────────────

export function SendCertificatesDialog({ conversation, open, onClose }: { conversation: ZaloConversation; open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const certs = useInboxCertificates(conversation.id, q, open);
  const send = useSendCertificates();
  const [picked, setPicked] = useState<number[]>([]);
  const [message, setMessage] = useState('');
  const items = certs.data?.items ?? [];

  useEffect(() => {
    if (open) {
      setQ('');
      setPicked([]);
      setMessage('');
    }
  }, [open]);

  const toggle = (id: number) => setPicked((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 5 ? [...s, id] : s));
  const submit = () =>
    picked.length &&
    send.mutateAsync({ id: conversation.id, certificateIds: picked, message: message.trim() || null }).then((r) => {
      toast.success(`Đã gửi ${r.files.length} chứng từ qua ${CHANNEL_LABELS[conversation.channel]}`);
      onClose();
    }, fail);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Gửi CO/CQ hàng hóa"
      description={conversation.customerCode ? `${conversation.customerCode} · ${conversation.customerName}` : 'Hội thoại chưa gắn khách'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Hủy</Button>
          <Button disabled={!picked.length || send.isPending} onClick={() => void submit()}>
            {send.isPending ? <Spinner /> : <Send />} Gửi {picked.length ? `(${picked.length})` : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute top-2.5 left-2.5 size-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mã hàng, tên, hãng, lô, số mẻ..." className="pl-8" aria-label="Tìm CO/CQ" />
        </div>
        {certs.data?.suggested && items.length > 0 && <p className="text-xs text-muted-foreground">Gợi ý theo hàng trong báo giá gần nhất của khách. Gõ để tìm chứng từ khác.</p>}
        {certs.isLoading ? (
          <Spinner />
        ) : items.length === 0 ? (
          <EmptyState title={q ? 'Không tìm thấy chứng từ' : 'Chưa có gợi ý'} description="Gõ mã hàng, hãng hoặc số lô để tìm CO/CQ." />
        ) : (
          <div className="max-h-80 space-y-2 overflow-auto">
            {items.map((c: InboxCert) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={picked.includes(c.id)}
                onClick={() => toggle(c.id)}
                className={cn('flex w-full items-start gap-3 rounded-lg border p-3 text-left', picked.includes(c.id) ? 'border-primary bg-primary/5' : 'hover:bg-muted/50')}
              >
                <FileCheck2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{c.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {[c.kindLabel, c.brandName ?? c.manufacturer, c.lotNo && `lô ${c.lotNo}`].filter(Boolean).join(' · ')}
                    {c.expired ? ' · đã hết hạn' : ''}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
        <div>
          <Label>Lời nhắn</Label>
          <Textarea rows={2} maxLength={1000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Em gửi Anh/Chị chứng từ CO/CQ của hàng hóa..." />
        </div>
        <p className="text-xs text-muted-foreground">Mỗi file là bản sao đóng chữ "BẢN SAO cấp cho {conversation.customerName ?? 'khách'}". Tối đa 5 chứng từ mỗi lần.</p>
      </div>
    </Dialog>
  );
}
